import { useCallback, useMemo, useState } from "react";
import { Card } from "../../../components/card/card";
import { Template } from "../../../components/template/template";
import { FaImages } from "react-icons/fa";
import ImageValidatorService from "../../../services/web/image-validator-service";
import ImageValidationCard from "./components/image-validation-card";

const ACCEPTED_MIME = ["image/jpeg", "image/png"];
const ACCEPTED_EXT = ["jpg", "jpeg", "png"];

const isAcceptedImage = (file) => {
    if (!file) return false;
    if (ACCEPTED_MIME.includes(file.type)) return true;
    const ext = (file.name?.split(".").pop() || "").toLowerCase();
    return ACCEPTED_EXT.includes(ext);
};

const parseInfo = (raw) => {
    const s = String(raw ?? "");
    if (!s || s === "false") return null;

    const parts = s.split("#");
    const obj = {};

    for (let i = 0; i < parts.length - 1; i += 2) {
        const key = (parts[i] ?? "").trim();
        const val = (parts[i + 1] ?? "").trim();
        if (key) obj[key] = val;
    }

    return obj;
};

export default function ImageValidator() {
    const [items, setItems] = useState([]);
    const hasFiles = items.length > 0;

    const statusLabel = useMemo(() => ({
        idle: "Aguardando validação",
        validating: "Validando...",
        valid: "Válida",
        invalid: "Inválida",
        error: "Erro",
    }), []);

    const statusClass = (st) => {
        if (st === "valid") return "border-3 border-green-600 bg-green-50";
        if (st === "invalid" || st === "error") return "border-3 border-red-700 bg-red-100";
        if (st === "validating") return "border-3 border-zinc-400 bg-zinc-50";
        return "border-zinc-200 bg-white";
    };

    const flashMark = useCallback((id, type) => {
        setItems(prev =>
            prev.map(p => (p.id === id ? { ...p, flash: type, flashVisible: false } : p))
        );

        window.setTimeout(() => {
            setItems(prev =>
            prev.map(p => (p.id === id ? { ...p, flashVisible: true } : p))
            );
        }, 20);

        window.setTimeout(() => {
            setItems(prev =>
            prev.map(p => (p.id === id ? { ...p, flashVisible: false } : p))
            );
        }, 1400);

        window.setTimeout(() => {
            setItems(prev =>
            prev.map(p => (p.id === id ? { ...p, flash: null } : p))
            );
        }, 1700);
    }, []);

    const validator = useCallback(async (id, file) => {
        setItems((prev) => prev.map((p) => (p.id === id ? { ...p, status: "validating", error: null, progress: 0 } : p)));

        try {
            const formData = new FormData();
            formData.append("image", file, file.name);

            const res = await ImageValidatorService.getValidation(formData, {
                onUploadProgress: (e) => {
                    const total = e.total || 0;
                    const pct = total ? Math.round((e.loaded * 100) / total) : 0;
                    
                    setItems((prev) => prev.map((p) => p.id === id ? { ...p, progress: Math.min(100, Math.max(0, pct)) } : p));
                },
            });

            setItems((prev) => prev.map((p) => (p.id === id ? { ...p, progress: 100 } : p)));

            const info = parseInfo(res?.data);

            if (info) {
                setItems((prev) => prev.map((p) => (p.id === id ? { ...p, status: "valid", info } : p)));
                flashMark(id, "success");
            } else {
                setItems((prev) => prev.map((p) => p.id === id ? { ...p, status: "invalid", info: null, error: "A imagem não pode ser validada" } : p));
                flashMark(id, "error");
            }
        } catch (err) {
            const status = err?.response?.status;
            const message = status && status !== 500 ? err?.response?.data?.message ?? "Não foi possível validar a imagem." : "Não foi possível validar a imagem."; 

            setItems((prev) => prev.map((p) => (p.id === id ? { ...p, status: "error", info: null, error: message} : p)));
        }
    }, []);

    const processFiles = useCallback((files) => {
        const valid = (files || []).filter(Boolean).filter(isAcceptedImage);
        if (!valid.length) return;

        valid.forEach((file) => {
            const id = `${file.name}-${file.size}-${file.lastModified}`;

            setItems((prev) => {
                if (prev.some((p) => p.id === id)) return prev;

                const url = URL.createObjectURL(file);
                return [...prev, { id, file, url, status: "idle", info: null, error: null, progress: 0, flash: null, flashVisible: false}]
            });
            setTimeout(() => validator(id, file), 0);
        })
    }, [validator]);

    const onClick = (e) => {
        e.preventDefault();
        const clickedInsidePreview = e.target.closest(".dropzone-preview");
        const clickedInsideButton = e.target.closest("button");
        if (clickedInsideButton || clickedInsidePreview) return;

        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/jpeg,image/png,.jpg,.jpeg,.png";
        input.multiple = true;
        input.onchange = (event) => processFiles([...event.target.files]);
        input.click();
    };

    const onDrop = (e) => {
        e.preventDefault();

        let files;
        if (e.dataTransfer.items) {
        files = [...e.dataTransfer.items]
            .filter((item) => item.kind === "file")
            .map((item) => item.getAsFile())
            .filter(Boolean);
        } else {
        files = [...e.dataTransfer.files];
        }

        processFiles(files);
    };

    return (
        <Template>
            <Template.Header>
                <Template.Title>Validação de imagens</Template.Title>
            </Template.Header>
            <Template.Body>
                <Card>
                    <Card.Body>
                        <div 
                            className="w-full min-h-55 flex flex-col items-center justify-center cursor-pointer px-5 rounded-md border border-zinc-200"
                            onClick={onClick}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={onDrop}
                        >
                            {!hasFiles ? (
                                <div className="text-center">
                                    <FaImages className="text-7xl inline-flex items-center justify-center" />
                                    <h4 className="text-center text-lg! font-bold mt-3">Clique aqui ou arraste as imagens para fazer o upload</h4>
                                </div>
                            ) : (
                                <div className="w-full flex flex-wrap justify-start gap-3">
                                    {items.map((it) => (
                                        <ImageValidationCard 
                                            key={it.id}
                                            item={it}
                                            statusClass={statusClass}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}